import { type Sca, createElement } from '@airwallex/components-sdk'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@ankor-io/ui-components/src/Button'
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '@ankor-io/ui-components/src/Dialog'
import { Loading } from '@ankor-io/ui-components/src/Loading'

import { useMinimumDisplayTime } from '@/hooks/useMinimumDisplayTime'
import { useSCA } from '@/providers/sca/useSCA'

const SCAModal = () => {
  const { user } = useKindeAuth()
  const { isOpen, scaSessionCode, handleSCASuccess, handleSCAError, handleSCACancel, ensureSDKInitialized } = useSCA()

  const [isLoading, setIsLoading] = useState(false)
  const showLoader = useMinimumDisplayTime(isLoading, 1000)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hasMountedRef = useRef(false)
  const scaElementRef = useRef<Sca.ScaElement | null>(null)

  useEffect(() => {
    if (!isOpen || !scaSessionCode) {
      return
    }

    const mountSCA = async () => {
      if (hasMountedRef.current) {
        return
      }
      hasMountedRef.current = true
      setError(null)
      setIsLoading(true)

      const container = document.getElementById('sca-verify-container')
      if (container) container.innerHTML = ''

      try {
        const sdk = await ensureSDKInitialized()
        if (!sdk) {
          throw new Error('Failed to initialize SDK')
        }

        // Create SCA verification element
        const scaElement: Sca.ScaElement = await createElement('scaVerify', {
          userEmail: user?.email || '',
          scaSessionCode: scaSessionCode,
        })
        scaElementRef.current = scaElement

        // Add event listeners
        scaElement.on('ready', () => {
          console.log('SCA component ready')
        })

        scaElement.on('verificationSucceed', (event) => {
          console.log('SCA verificationSucceed payload:', event)
          if (event && 'token' in event) {
            const scaToken = event.token
            if (scaToken) {
              handleSCASuccess(scaToken)
            } else {
              handleSCAError(new Error('No SCA token received from verification'))
            }
          } else {
            handleSCAError(new Error('Invalid verification success event'))
          }
        })

        scaElement.on('verificationFailed', (event) => {
          if (event && 'reason' in event) {
            handleSCAError(new Error(event.reason || 'SCA verification failed'))
          } else {
            handleSCAError(new Error('SCA verification failed'))
          }
        })

        scaElement.on('scaSetupSucceed', (event) => {
          console.info('SCA setup successful', event)
        })

        scaElement.on('resetPasscodeSucceed', () => {
          console.info('SCA reset passcode successful')
        })

        scaElement.on('cancel', () => {
          handleSCACancel()
        })

        // @ts-expect-error 'fullscreen' is not in ScaEventCode, but the SDK emits it
        scaElement.on('fullscreen', () => {
          setIsFullscreen(true)
        })

        // @ts-expect-error 'quitFullscreen' is not in ScaEventCode, but the SDK emits it
        scaElement.on('quitFullscreen', () => {
          setIsFullscreen(false)
        })

        scaElement.on('error', (event) => {
          console.error('SCA error:', event)
        })

        scaElement.mount('sca-verify-container')
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize SCA component'
        setError(errorMessage)
        console.error('SCA Component Error:', err)
        handleSCAError(err instanceof Error ? err : new Error(errorMessage))
      } finally {
        setIsLoading(false)
      }
    }

    mountSCA()
    return () => {
      // cleanup when modal closes or session code changes
      scaElementRef.current?.unmount?.()
      const maybeDestroy = (scaElementRef.current as unknown as { destroy?: () => void })?.destroy
      if (typeof maybeDestroy === 'function') {
        maybeDestroy()
      }
      scaElementRef.current = null
      const container = document.getElementById('sca-verify-container')
      if (container) container.innerHTML = ''
      hasMountedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, scaSessionCode])

  if (!isOpen) {
    return null
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleSCACancel}
      size={isFullscreen ? 'fullscreen' : 'lg'}
    >
      <DialogTitle>
        <div className="flex justify-between items-center">
          <span>Strong Customer Authentication</span>
          <Button
            plain
            onClick={handleSCACancel}
            aria-label="Close"
          >
            ×
          </Button>
        </div>
      </DialogTitle>

      <DialogDescription>
        Please complete the authentication process to continue with your transaction.
      </DialogDescription>

      <div className="relative mt-2">
        <div
          key={scaSessionCode ?? 'closed'}
          id="sca-verify-container"
          className="h-full rounded-lg overflow-hidden p-2 bg-white"
        />

        {showLoader && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/80">
            <Loading />
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/80">
            <div className="text-center">
              <p className="text-red-600 dark:text-red-400 mb-4">Failed to load authentication</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{error}</p>
            </div>
          </div>
        )}
      </div>

      <DialogActions>
        <Button
          outline
          onClick={handleSCACancel}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SCAModal
